import React, { useEffect, useState } from "react";
import { useIntl } from "react-intl";
import { useQuery } from "react-query";
import { CustomAutocomplete } from "../../components/Inputs/CustomAutocomplete";
import { basicFetch, GenericObject } from "../../services/Price";
import { useDebounce } from "../../hooks/debounce";
import { PropertyOptionType } from "../interfaces";

const SX_STYLE = {
  flex: 1,
  height: 24,
  alignSelf: "baseline",
  borderRadius: "4px",
  "& .MuiInputBase-root.MuiOutlinedInput-root.MuiInputBase-sizeSmall": {
    backgroundColor: "background.input",
    height: "24px",
    pt: 0,
    pb: 0,
  },
  "& .MuiInput-underline:before": {
    content: "unset",
  },
  "& .MuiInputLabel-outlined.MuiInputLabel-shrink": {
    transform: "translate(0px, -20px)",
    fontWeight: 700,
  },
  "& label": {
    display: "none",
  },
  "& input": {
    fontSize: "0.8125rem",
    whiteSpace: "nowrap",
    overflow: "hidden",
    textOverflow: "ellipsis",
  },
  "& .MuiSelect-select": {
    fontSize: "0.875rem",
    height: "0px",
  },
  "& .MuiSelect-icon, & .MuiIconButton-root": {
    color: "text.secondary",
  },
};

export const listProperties = async (params: any, searchProperty?: number) => {
  if (searchProperty) {
    return basicFetch<any>(`/api/properties/${searchProperty}/`, params);
  }

  return basicFetch<any>("/api/properties/", params);
};

/**
 * Allows the selection of a property
 * @param onChange
 * @param value
 * @constructor
 */
const PropertySelector = ({
  onChange,
  value,
}: {
  onChange: (value: number) => void;
  value?: number;
}) => {
  const intl = useIntl();
  const [options, setOptions] = useState<PropertyOptionType[]>([]);
  const [selectedProperty, setSelectedProperty] =
    useState<PropertyOptionType | null>(null);
  const [searchValue, setSearchValue] = useState<string | null>(null);
  //Avoids autoselect on input search
  const [inputHasChanged, setInputHasChange] = useState<boolean>(false);

  const debounceSearchValue = useDebounce(searchValue, 400);

  const queryProperties = useQuery(["properties", debounceSearchValue], () =>
    listProperties({
      page_size: 100,
      search: debounceSearchValue,
    })
  );

  /**
   * Use this fetch to search pre-choosen property with props "value" in properties
   * In some case, clients have more than 100 properties so the current pre-choosen
   * property may not be in first 100 properties and that cause a bug. So we search
   * in another fetch the current property to be sure it exist
   */
  // Without an id, `listProperties` falls back to the list endpoint and its
  // paginated envelope would be appended to `options` as a fake property.
  const queryPropertiesSearch = useQuery<GenericObject>(
    ["propertiesSearch", value],
    () => listProperties({}, value),
    { enabled: !!value }
  );

  /**
   * Handles the change event for the input
   *
   * We check there was an actual user change to avoid triggering on a search.
   * @param event
   * @param newValue
   */
  const handleChange = (
    event: React.SyntheticEvent<Element, Event>,
    newValue: PropertyOptionType | null
  ) => {
    if (
      event.type === "keydown" &&
      (event as React.KeyboardEvent).key === "Backspace"
    ) {
      return;
    }
    if (newValue) {
      setInputHasChange(false);
      onChange(newValue.id);
    }
  };

  /**
   * Assigne properties data to options list
   *
   * We check if queryPropertiesSearch exist or not in this customer
   * If not just use queryProperties
   * If exist, check if already in queryProperties list
   *  if not add it
   *  if already exist just use queryProperties
   *
   * By doing this, we ensure that the property is in the list and therefore displayed in the selector.
   */
  useEffect(() => {
    if (queryProperties.data?.results && !queryPropertiesSearch.isLoading) {
      if (queryPropertiesSearch.isSuccess) {
        const findCurrProperty = queryProperties.data.results.find(
          (elem) => elem.id === queryPropertiesSearch.data.id
        );

        if (findCurrProperty) {
          setOptions([...queryProperties.data?.results]);
        } else {
          setOptions([
            ...queryProperties.data?.results,
            queryPropertiesSearch.data,
          ]);
        }
      } else {
        setOptions([...queryProperties.data.results]);
      }
    }
  }, [
    queryProperties.data?.results,
    queryPropertiesSearch.isLoading,
    queryPropertiesSearch.data,
    queryPropertiesSearch.isSuccess,
  ]);

  /**
   * Sets the default selected property at initialization
   *
   * Allows to pass only the id as argument and gets the corresponding
   * property from the options. If there is no match, we return the first one.
   */
  useEffect(() => {
    if (options && options[0] && !inputHasChanged) {
      let property = options[0];

      if (value) {
        const findCurrProperty = options.find((elem) => elem.id === value);
        if (findCurrProperty) {
          property = findCurrProperty;
        } else {
          onChange(property.id);
        }
      } else {
        onChange(property.id);
      }
      setSelectedProperty(property);
    }
  }, [onChange, options, value, inputHasChanged]);

  return (
    <CustomAutocomplete
      disablePortal
      formStyle={false}
      getOptionLabel={(option) => option.hebergement_nom_court}
      isOptionEqualToValue={(option, value) => option.id === value.id}
      noOptionsText={intl.formatMessage({ id: "NO_DATA" })}
      options={options}
      onChange={handleChange}
      onInputChange={(event, newValue, reason) => {
        if ((event && event.type === "change") || reason === "clear") {
          setSearchValue(newValue);
          setInputHasChange(true);
        }
      }}
      renderOption={(props, option) => (
        <li {...props} key={option.id}>
          {option.hebergement_nom_court}
        </li>
      )}
      placeholder={intl.formatMessage({ id: "SELECT_PROPERTY" })}
      textFieldSx={{ margin: 0 }}
      sx={SX_STYLE}
      value={selectedProperty}
    />
  );
};

export default PropertySelector;
